import { DatePipe, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Locale, WINDOW } from '@biletix/core/common';
import { RouteParameter } from '@biletix/core/common/constants/route-parameter.const';
import { ImageUrlManager } from '@biletix/core/common/helper/image-url-manager';
import { EventsDetailByVenue } from '@biletix/core/models/event/events-detail-by-venue.model';
import { EventService } from '@biletix/core/services/event.service';
import { I18nService } from '@biletix/core/services/i18n.service';
import { TranslateService } from '@ngx-translate/core';
import { BtxPerformanceListItem } from "@biletix/core/models/event/performance-list-item.model";
import { ImageProcessing } from '@biletix/core/common/utils/image-processing';
import { AbstractionComponent } from '@biletix/core/common/abstraction-component';
import { ShareButtonsConfig } from '@biletix/shared/components/share-button/share-button.component';
import { environment } from '@biletix/env';
import { MatDialog } from '@angular/material/dialog';
import { VenueParkingModalComponent } from './venue-parking-modal/venue-parking-modal.component';
import { SeoService } from '@biletix/core/services/seo.service';
import { CanonicalService } from '@biletix/core/services/canonical.service';
import { SeoPagePath, SeoUrlService } from '@biletix/core/services/seo-url.service';
import { GPT_AD_SLOT_IDS, GptAdService } from '@biletix/core/services/gpt-ad.service';
import { StructuredDataService } from '@biletix/core/services/structured-data.service';
import {
  SCHEMA_DATE_FORMAT,
  SCHEMA_TIMEZONE,
  buildEventItemList,
  pruneEmpty,
  toPlainText,
} from '@biletix/core/common/utils/structured-data.util';
import { resolveListItemPrice } from '@biletix/core/common/utils/ga4-ecommerce.util';

@Component({
  standalone: false,
  selector: 'btx-venue-event-list',
  templateUrl: './venue-event-list.component.html',
  styleUrls: ['./venue-event-list.component.scss'],
})
export class VenueEventListComponent extends AbstractionComponent implements OnInit, AfterViewInit {
  locales: Locale[];
  currentLocale: Locale;

  calendarOrListStatus: boolean = false;
  date: string | number | Date = new Date();
  @Input() region?: string;
  @Input() lang?: string;
  venueName: string = '';
  venueLogoImage: string = '';
  venueSmallLogoImage: string = '';
  venueMidLogoImage: string = '';
  venueWideImage: string = '';
  venueDescription: string = '';
  venueWebUrl: string = '';
  adress: string = '';
  cityName: string = '';
  townName: string = '';
  latitude: number | undefined;
  longitude: number | undefined;
  seatPlanImage: string = '';
  parkingInfo: string = '';
  venueImageSrc!: string;
  eventNoSrc!: string;
  photoItems: string[] = [];
  mobileGallery: string[] = [];
  config!: ShareButtonsConfig;
  environment = environment;
  stay22CampaignId: string = 'confirmationpage';
  stay22Lat: string = '';
  stay22Long: string = '';
  stay22Venue: string = '';

  color: string = '#00000';
  eventsDetailListByVenue!: EventsDetailByVenue;
  performanceItems: BtxPerformanceListItem[] = [];

  /** JSON-LD script'inin id'si; aynı id ile yazmak bloğu çoğaltmaz (bkz. StructuredDataService). */
  private static readonly SCHEMA_ELEMENT_ID = 'btx-venue-schema';

  /**
   * Rotadaki dil parametresi. `@Input() lang` yalnız üst bileşen bağladığında
   * dolduğu için SEO tarafında rota parametresi kullanılır (bkz. initSeo).
   */
  private langParam: string = '';
  constructor(
    @Inject(WINDOW) private window: Window,
    @Inject(PLATFORM_ID) private platformId: Object,
    private gptAdService: GptAdService,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private i18nService: I18nService,
    private seo: SeoService,
    private canonicalService: CanonicalService,
    private seoUrlService: SeoUrlService,
    private structuredDataService: StructuredDataService,
    private dialog: MatDialog
  ) {
    super();
    this.locales = this.i18nService.getAvailableLocales();
    this.currentLocale = this.i18nService.currentLocale;
    this.langParam = this.activatedRoute.snapshot.params[RouteParameter.PARAMS.LANGUAGE];
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Yan sütundaki 300x600 slot şablonda koşulsuz; mobilde alan CSS ile
      // gizliyse servis görünürlük kontrolünde isteği atlıyor.
      this.gptAdService.displayAndRefreshAds([GPT_AD_SLOT_IDS.SIDE_BANNER_300X600]);
    }
  }

  ngOnInit(): void {

    this.initializeVariables();

    this.activatedRoute.data.pipe(this.takeUntilDestroy())
      .subscribe(({ eventsDetailByVenue }) => {
        if (!eventsDetailByVenue) return;
        this.eventsDetailListByVenue = eventsDetailByVenue;
        this.performanceItems = this.groupEventForList();
        this.calendarOrListStatus = false;
        this.venueName = eventsDetailByVenue.venueName;
        this.venueLogoImage = eventsDetailByVenue.venueLogoImage;
        this.venueWideImage = eventsDetailByVenue.venueWideImage;
        const rawLogo = eventsDetailByVenue.venueLogoImage;
        const rawWide = eventsDetailByVenue.venueWideImage;

        this.venueDescription = eventsDetailByVenue.venueDescription;
        this.venueWebUrl = eventsDetailByVenue.venueWebsite;
        this.adress = eventsDetailByVenue.address;
        this.cityName = eventsDetailByVenue.cityName;
        this.townName = eventsDetailByVenue.townName;
        this.latitude = eventsDetailByVenue.latitude;
        this.longitude = eventsDetailByVenue.longitude;
        this.stay22Lat = eventsDetailByVenue.latitude ? String(eventsDetailByVenue.latitude) : '';
        this.stay22Long = eventsDetailByVenue.longitude ? String(eventsDetailByVenue.longitude) : '';
        this.stay22Venue = eventsDetailByVenue.venueName || '';
        this.seatPlanImage = eventsDetailByVenue.seatPlan || 'noseat.gif';
        this.parkingInfo = eventsDetailByVenue.parkingInfo || '';

        this.venueWideImage = this.getWideImage(rawWide);
        this.venueLogoImage = this.getVenueImage(rawLogo);
        this.venueSmallLogoImage = this.getSmallVenueImage(rawLogo);
        this.venueMidLogoImage = this.getMidVenueImage(rawLogo);

        this.venueImageSrc = this.venueLogoImage;
        this.photoItems = this.getVenueImages();
        this.eventNoSrc = this.getNoImage();
        this.mobileGallery.push(this.venueImageSrc, ...this.photoItems);
        this.initSeo(eventsDetailByVenue);
      });

    console.log(this.stay22Lat, this.stay22Long, "side-image-container");

  }

  initializeVariables() {
    this.config = {
      url: this.window.location.href,
      description: this.venueDescription,
      title: this.venueDescription,
      image: this.getVenueImage(this.venueLogoImage)
    };
  }

  /**
   * Mekan sayfasının sosyal medya paylaşım tag'larını set eder.
   *
   * Bu sayfa SSR ile servis ediliyor (bkz. src/server.ts içindeki `ssrRoutes`
   * allowlist'inde `/mekan` var) ve WhatsApp/Slack/Facebook crawler'ları
   * JavaScript çalıştırmadığı için tag'ların sunucudan gelen HTML'de hazır
   * olması gerekir. Resolver verisi component oluşmadan çözüldüğünden bu blok
   * SSR render'ı sırasında da çalışır.
   *
   * Görsel `toSocialShareImage()` üzerinden geçirilir: mekan logoları .avif
   * servis edilebiliyor ve crawler'lar AVIF decode edemiyor.
   *
   * Kanonik URL slug'SIZ formdur (`mekan/{venueId}/TURKIYE/{dil}`). Rota slug'lı
   * varyantı da kabul ediyor (bkz. app-routing-module.ts:322) ama slug servis
   * verisinde yok — `EventsDetailByVenue`'da `keyUrlText` alanı bulunmuyor, iç
   * link'ler slug'ı etkinlik detayındaki `venueKeyUrlText`'ten alıyor. Kanonik
   * sinyalin istenen URL'e göre değişmemesi gerektiği için yalnızca veriden
   * türetilebilen slug'sız form kanonik seçilir; iki varyant böylece tek URL'de
   * birleşir.
   */
  private initSeo(venue: EventsDetailByVenue): void {
    const pagePath: SeoPagePath = { segments: ['mekan', venue.venueId] };
    const canonicalUrl = this.seoUrlService.buildCanonicalUrl(pagePath, this.langParam);

    this.translateService
      .stream(['shared.labels.titleTickets', 'shared.labels.siteKeywords'])
      .pipe(this.takeUntilDestroy())
      .subscribe((translations) => {
        this.seo.setTags({
          title: venue.venueName + (translations['shared.labels.titleTickets'] ?? ''),
          titleSuffix: '',
          description: venue.venueDescription,
          image: ImageUrlManager.toSocialShareImage(this.venueImageSrc),
          keywords: venue.venueName + (translations['shared.labels.siteKeywords'] ?? ''),
          // og:url canonical ile aynı olmalı; document.URL SSR'da http üretiyordu.
          url: canonicalUrl,
        });
      });

    this.canonicalService.setCanonicalUrl(canonicalUrl);
    this.canonicalService.setAlternateUrls(this.seoUrlService.buildAlternateUrls(pagePath));
    this.addSchema(venue, canonicalUrl);
  }

  /**
   * Mekan sayfasının JSON-LD'sini yayınlar: `Place` + etkinlik `ItemList`'i.
   *
   * Tek script içinde iki blok (dizi) yayınlanır — sayfa iki şeyi birden
   * anlatıyor: mekanın kendisi (adres/koordinat) ve orada satışta olan
   * etkinlikler. `initSeo()` içinden, yani resolver verisi hazır olduğunda
   * çağrılır; böylece blok SSR HTML'inde de bulunur.
   */
  private addSchema(venue: EventsDetailByVenue, canonicalUrl: string): void {
    const blocks = [
      this.createPlaceSchema(venue, canonicalUrl),
      this.createVenueEventListSchema(venue),
    ].filter((block) => !!block);

    this.structuredDataService.setJsonLd(
      VenueEventListComponent.SCHEMA_ELEMENT_ID,
      blocks.length ? blocks : null,
    );
  }

  /**
   * Mekanı `Place` olarak tanımlar.
   *
   * `geo` yalnız iki koordinat da varsa yazılır: tek başına enlem geçersiz bir
   * `GeoCoordinates` üretir. `sameAs` mekanın kendi sitesidir — `url` Biletix
   * sayfasını gösterdiği için ikisi karıştırılmaz.
   */
  private createPlaceSchema(
    venue: EventsDetailByVenue,
    canonicalUrl: string,
  ): Record<string, unknown> | null {
    if (!venue?.venueName) {
      return null;
    }

    const hasGeo = !!venue.latitude && !!venue.longitude;

    return pruneEmpty<Record<string, unknown>>({
      '@context': 'https://schema.org',
      '@type': 'Place',
      '@id': canonicalUrl,
      url: canonicalUrl,
      name: venue.venueName,
      description: toPlainText(venue.venueDescription),
      image: ImageUrlManager.toSocialShareImage(this.venueImageSrc),
      sameAs: venue.venueWebsite,
      // `addressLocality` şehirdir; `townName` ilçe adı ve sıklıkla "MERKEZ" gibi
      // şehirden bağımsız anlamı olmayan bir değer taşıyor, bu yüzden sokak
      // adresinin sonuna eklenir, locality olarak yayınlanmaz.
      address: {
        '@type': 'PostalAddress',
        streetAddress: [venue.address, venue.townName].filter((part) => !!part).join(', '),
        addressLocality: venue.cityName,
        addressCountry: 'TR',
      },
      geo: hasGeo
        ? {
            '@type': 'GeoCoordinates',
            latitude: venue.latitude,
            longitude: venue.longitude,
          }
        : undefined,
    });
  }

  /** Mekanda satışta olan etkinlikler; kayıtlar kendi kanonik URL'lerine bağlanır. */
  private createVenueEventListSchema(
    venue: EventsDetailByVenue,
  ): Record<string, unknown> | null {
    return buildEventItemList(
      venue?.venueName ?? '',
      (this.performanceItems ?? []).map((item) => ({
        url: this.seoUrlService.buildCanonicalUrl(
          { segments: ['etkinlik', item.eventCode], slug: item.keyUrlText },
          this.langParam,
        ),
        name: item.eventName || item.longName || item.shortName,
        startDate: this.datePipe.transform(
          item.performanceDate,
          SCHEMA_DATE_FORMAT,
          SCHEMA_TIMEZONE,
        ),
        venueName: item.venueName || venue?.venueName,
        venueCity: item.venueCity || venue?.cityName,
        price: resolveListItemPrice(item.minPrice, item.priceInfo),
      })),
    );
  }

  getVenueImage(path?: string | null): string {
    if (!path || !path.trim() || path.includes('noimage')) {
      return ImageUrlManager.createEventImage('noimage.gif');
    }
    return ImageUrlManager.createVenueImage(path);
  }

  getSmallVenueImage(path?: string | null): string {
    if (!path || !path.trim() || path.includes('noimage')) {
      return ImageUrlManager.createEventImage('noimage.gif');
    }
    return ImageUrlManager.createSmallVenueImage(path);
  }

  getMidVenueImage(path?: string | null): string {
    if (!path || !path.trim() || path.includes('noimage')) {
      return ImageUrlManager.createEventImage('noimage.gif');
    }
    return ImageUrlManager.createMidVenueImage(path);
  }

  isAvifImage(src?: string | null): boolean {
    if (!src) {
      return false;
    }
    return src.toLowerCase().endsWith('.avif');
  }

  private getVenueImages(): string[] {
    const images = this.eventsDetailListByVenue.venueImages ?? [];
    return images.map((url: string) => ImageUrlManager.create(url, environment.venuePhotosBasePath));
  }

  isPhotoGalleryVisible(): boolean {
    return this.photoItems.length > 0;
  }

  getWideImage(path?: string | null): string {
    if (!path || !path.trim() || path.includes('noimage')) {
      return ImageUrlManager.createEventImage('noimage.gif');
    }
    return ImageUrlManager.createVenueWideImage(path);
  }

  getNoImage(): string {
    return ImageUrlManager.createEventImage("noimage.gif");
  }

  isNoImage(src?: string | null): boolean {
    return !src || !src.trim() || src.includes('noimage');
  }

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement && !imgElement.src.includes('noimage.gif')) {
      imgElement.src = ImageUrlManager.createEventImage('noimage.gif');
    }
  }

  getHeroBannerBg(): string {
    if (this.venueWideImage && !this.venueWideImage.includes('noimage')) {
      return `url('${this.venueWideImage}')`;
    }
    if (this.venueLogoImage && !this.venueLogoImage.includes('noimage')) {
      return `url('${this.venueLogoImage}')`;
    }
    return `url('assets/images/venue-hero-stage.jpg')`;
  }

  groupEventForList(): BtxPerformanceListItem[] {
    const eventMap: Map<string, BtxPerformanceListItem> = new Map<string, BtxPerformanceListItem>();

    const priorityList = ['s01_onsale', 's03_soon', 's05_postponed', 's02_soldout', 's12_checkbacklater',
      's04_canceled', 's00_closed', 's06_boxoffice', 's16_rescheduled', 's17_venue_changed', 's14_other_channels', 's13_old_over'];


    this.eventsDetailListByVenue.performanceItems.forEach((event: BtxPerformanceListItem) => {
      if (!eventMap.has(event.eventCode)) {
        eventMap.set(event.eventCode, event);
        eventMap.get(event.eventCode)!.firstPerformanceDate = event.performanceDate;
        eventMap.get(event.eventCode)!.lastPerformanceDate = event.performanceDate;
      } else {
        const firstEvent = eventMap.get(event.eventCode);
        if (firstEvent && firstEvent.firstPerformanceDate && firstEvent.firstPerformanceDate > event.performanceDate) {
          eventMap.get(event.eventCode)!.firstPerformanceDate = event.performanceDate;
        }
        if (firstEvent && firstEvent.lastPerformanceDate && firstEvent.lastPerformanceDate < event.performanceDate) {
          eventMap.get(event.eventCode)!.lastPerformanceDate = event.performanceDate;
        }
        if (priorityList.indexOf(firstEvent!.status) > priorityList.indexOf(event.status)) {
          eventMap.get(event.eventCode)!.status = event.status;
        }
      }
    });
    return [...eventMap.values()];
  }

  onLoadImage(imageRef: HTMLImageElement) {
    if (imageRef) {
      const a = ImageProcessing.getAverageRGB(imageRef);
      this.color = a.hex();
    }
  }

  getAdressUrl() {
    return 'https://www.google.com/maps?q=' + this.latitude + ',' + this.longitude;
  }

  openParkingModal(): void {
    this.dialog.open(VenueParkingModalComponent, {
      width: '95vw',
      maxWidth: '500px',
      data: {
        parkingInfo: this.parkingInfo
      }
    });
  }
}
